import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import {
  GUIA_ICONES,
  GUIA_ORIGENS,
  GUIAS_BOTAO_ROTULO,
  GUIAS_MAX_ITENS,
  GUIAS_TITULO,
  type GuiaItem,
} from './helpers';

const messages = defineMessages({
  titleBlock: { id: 'mpf_guias_block', defaultMessage: 'Guias' },
  titulo: { id: 'mpf_guias_titulo', defaultMessage: 'Título da faixa' },
  tituloHelp: {
    id: 'mpf_guias_titulo_help',
    defaultMessage: 'Em branco, usa "{padrao}".',
  },
  itens: { id: 'mpf_guias_itens', defaultMessage: 'Guias' },
  itensHelp: {
    id: 'mpf_guias_itens_help',
    defaultMessage:
      'Até {max} guias, três por linha no computador. Guia sem título ou sem destino não aparece no site.',
  },
  item: { id: 'mpf_guias_item', defaultMessage: 'Guia' },
  origem: { id: 'mpf_guias_origem', defaultMessage: 'Origem' },
  origemHelp: {
    id: 'mpf_guias_origem_help',
    defaultMessage: 'Selo exibido acima do título.',
  },
  icone: { id: 'mpf_guias_icone', defaultMessage: 'Ícone' },
  iconeHelp: {
    id: 'mpf_guias_icone_help',
    defaultMessage: 'Em branco, usa o ícone da origem.',
  },
  itemTitulo: { id: 'mpf_guias_item_titulo', defaultMessage: 'Título' },
  descricao: { id: 'mpf_guias_descricao', defaultMessage: 'Descrição' },
  href: { id: 'mpf_guias_href', defaultMessage: 'Destino' },
  hrefHelp: {
    id: 'mpf_guias_href_help',
    defaultMessage: 'Página do site ou endereço externo.',
  },
  botaoRotulo: {
    id: 'mpf_guias_botao_rotulo',
    defaultMessage: 'Rótulo do botão',
  },
  botaoRotuloHelp: {
    id: 'mpf_guias_botao_rotulo_help',
    defaultMessage: 'Em branco, usa "{padrao}".',
  },
  externo: { id: 'mpf_guias_externo', defaultMessage: 'Site externo' },
  externoHelp: {
    id: 'mpf_guias_externo_help',
    defaultMessage:
      'Ligue quando o destino está fora do portal do MPF: o selo ganha "site externo" e o botão, o ícone de link externo.',
  },
});

const ICONE_ROTULOS: Record<(typeof GUIA_ICONES)[number], string> = {
  IdentificationCard: 'Documento de identidade',
  FileText: 'Documento',
  BookOpen: 'Livro aberto',
  Certificate: 'Certificado',
  Info: 'Informação',
  Question: 'Pergunta',
  ShieldCheck: 'Escudo',
};

const guiaItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  itemTitle: (guia: GuiaItem, index: number) =>
    guia?.titulo?.trim() ||
    `${intl.formatMessage(messages.item)} #${index + 1}`,
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'origem',
        'icone',
        'titulo',
        'descricao',
        'href',
        'botaoRotulo',
        'externo',
      ],
    },
  ],
  properties: {
    origem: {
      title: intl.formatMessage(messages.origem),
      description: intl.formatMessage(messages.origemHelp),
      choices: Object.entries(GUIA_ORIGENS),
      default: 'pagina-mpf',
    },
    icone: {
      title: intl.formatMessage(messages.icone),
      description: intl.formatMessage(messages.iconeHelp),
      choices: GUIA_ICONES.map((nome) => [nome, ICONE_ROTULOS[nome]]),
    },
    titulo: { title: intl.formatMessage(messages.itemTitulo) },
    descricao: {
      title: intl.formatMessage(messages.descricao),
      widget: 'textarea',
    },
    href: {
      title: intl.formatMessage(messages.href),
      description: intl.formatMessage(messages.hrefHelp),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['@id', '@type', 'Title'],
      allowExternals: true,
      initialPath: '/',
    },
    botaoRotulo: {
      title: intl.formatMessage(messages.botaoRotulo),
      description: intl.formatMessage(messages.botaoRotuloHelp, {
        padrao: GUIAS_BOTAO_ROTULO,
      }),
      placeholder: GUIAS_BOTAO_ROTULO,
    },
    externo: {
      title: intl.formatMessage(messages.externo),
      description: intl.formatMessage(messages.externoHelp),
      type: 'boolean',
    },
  },
  required: ['titulo'],
});

export const mpfGuiasSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [{ id: 'default', title: 'Default', fields: ['titulo', 'itens'] }],
  properties: {
    titulo: {
      title: intl.formatMessage(messages.titulo),
      description: intl.formatMessage(messages.tituloHelp, {
        padrao: GUIAS_TITULO,
      }),
      placeholder: GUIAS_TITULO,
    },
    itens: {
      title: intl.formatMessage(messages.itens),
      description: intl.formatMessage(messages.itensHelp, {
        max: GUIAS_MAX_ITENS,
      }),
      widget: 'object_list',
      schema: guiaItemSchema({ intl }),
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfGuiasSchema;
